/**
 * CRUD for articleCategories | articlePetTags — table + expandable forms (like Articles admin).
 */

import { useCallback, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../../app/firebase";
import { useMediaQuery } from "react-responsive";
import {
  BackScreenDiv,
  Button,
  ContentDiv,
  Div,
  H1,
  HeaderDiv,
  ScreenDiv,
  TableColumnDiv,
  TableRowDivLookups,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableRowDiv,
} from "../../../styles/styles";
import { pickLang } from "../../../utils/i18nDisplay";
import AdminLookupFormPanel, {
  LOOKUP_CREATE_FORM_ID,
  lookupEditFormId,
} from "./adminLookupFormPanel";

const LANGUAGES = ["en", "ru", "sr"];

const emptyTriple = () => ({ en: "", ru: "", sr: "" });

const emptyFormData = () => ({
  id: "",
  name: emptyTriple(),
  slug: emptyTriple(),
  order: 0,
});

const formDataFromRow = (row) => ({
  id: row.id,
  name: { ...emptyTriple(), ...(row.name || {}) },
  slug: { ...emptyTriple(), ...(row.slug || {}) },
  order: row.order ?? 0,
});

const slugPreview = (row) => {
  const parts = LANGUAGES.map((lng) => row.slug?.[lng]?.trim()).filter(Boolean);
  return parts[0] || "—";
};

function Inner({ heading, collectionName }) {
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(emptyFormData);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyFormData);

  const reload = useCallback(async () => {
    if (!db) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, collectionName));
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(list);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  useEffect(() => {
    reload();
  }, [reload]);

  const nextAutoOrder = () =>
    items.length ? Math.max(...items.map((i) => i.order ?? 0)) + 1 : 0;

  const sanitizeNamesTriple = (t) => {
    const obj = {};
    LANGUAGES.forEach((lng) => {
      const v = (t?.[lng] || "").trim();
      if (v) obj[lng] = v;
    });
    return obj;
  };

  const sanitizeSlugTriple = (t) => {
    const obj = {};
    LANGUAGES.forEach((lng) => {
      const v = (t?.[lng] || "").trim();
      if (v) obj[lng] = v;
    });
    return Object.keys(obj).length ? obj : null;
  };

  const rebuildOrders = async (rows) =>
    Promise.all(
      rows.map((r, ix) =>
        setDoc(
          doc(db, collectionName, r.id),
          { order: ix, updatedAt: serverTimestamp() },
          { merge: true },
        ),
      ),
    );

  const persistItem = async (formData, existingId = null) => {
    if (!db) return false;
    const names = sanitizeNamesTriple(formData.name);
    if (!Object.keys(names).length) {
      setError("Name required on at least one language.");
      return false;
    }

    setSaving(true);
    setError(null);
    try {
      let idDoc = existingId || formData.id.trim();
      if (!idDoc) {
        idDoc = doc(collection(db, collectionName)).id;
      } else if (!existingId && !/^[a-zA-Z0-9_-]+$/.test(idDoc)) {
        setError("ID: letters, digits, hyphen, underscore only.");
        return false;
      }

      const orderVal =
        formData.order !== 0
          ? Number(formData.order) || 0
          : existingId
            ? 0
            : nextAutoOrder();

      await setDoc(
        doc(db, collectionName, idDoc),
        {
          name: names,
          slug: sanitizeSlugTriple(formData.slug),
          order: orderVal,
          updatedAt: serverTimestamp(),
          ...(existingId ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true },
      );
      await reload();
      return true;
    } catch (er) {
      setError(er.message || String(er));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setCreateFormData({ ...emptyFormData(), order: nextAutoOrder() });
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateFormData(emptyFormData());
  };

  const handleCreateSubmit = async () => {
    const ok = await persistItem(createFormData);
    if (ok) closeCreate();
  };

  const handleEditToggle = (row) => {
    if (editingId === row.id) {
      setEditingId(null);
      setEditFormData(emptyFormData());
      return;
    }
    setCreateOpen(false);
    setEditingId(row.id);
    setEditFormData(formDataFromRow(row));
  };

  const handleEditSubmit = async () => {
    if (!editingId) return;
    const ok = await persistItem(editFormData, editingId);
    if (ok) {
      setEditingId(null);
      setEditFormData(emptyFormData());
    }
  };

  const handleDelete = async () => {
    if (!db || !editingId) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteDoc(doc(db, collectionName, editingId));
      const rest = items.filter((x) => x.id !== editingId);
      await rebuildOrders(rest);
      await reload();
      setEditingId(null);
      setEditFormData(emptyFormData());
    } catch (er) {
      setError(er.message || String(er));
    } finally {
      setDeleting(false);
    }
  };

  const moveRow = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSaving(true);
    try {
      await rebuildOrders(arr);
      await reload();
    } catch (er) {
      setError(er.message || String(er));
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || deleting;

  return (
    <>
      <BackScreenDiv grayImage />
      <ScreenDiv column mobileJustifySpace>
        <HeaderDiv />
        <ContentDiv
          gap={!isDesktop && "5px"}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
        >
          <Div row width="100%" justify="space-between" align="center">
            {isDesktop && <H1>{heading}</H1>}
            <Button
              type="button"
              onClick={reload}
              disabled={loading}
              width="auto"
              greenButton
            >
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </Div>

          {error ? (
            <div role="alert" style={{ color: "#c62828", marginBottom: 8 }}>
              {error}
            </div>
          ) : null}

          <Div row gap="10px" justifyStart width="100%" height="auto">
            <Button
              type="button"
              greenButton
              width="auto"
              disabled={createOpen}
              onClick={openCreate}
            >
              Create
            </Button>
            {createOpen ? (
              <>
                <Button
                  type="submit"
                  form={LOOKUP_CREATE_FORM_ID}
                  greenButton
                  width="auto"
                  disabled={busy}
                >
                  {saving ? "Saving…" : "Create"}
                </Button>
                <Button
                  type="button"
                  outlineButton
                  width="auto"
                  onClick={closeCreate}
                >
                  Close
                </Button>
              </>
            ) : null}
          </Div>

          {createOpen ? (
            <AdminLookupFormPanel
              title={`Create — ${heading}`}
              formId={LOOKUP_CREATE_FORM_ID}
              formData={createFormData}
              setFormData={setCreateFormData}
              isEdit={false}
              onCancel={closeCreate}
              onSubmit={handleCreateSubmit}
              saving={saving}
              deleting={false}
            />
          ) : null}

          <Div width="100%">
            <UserTableDiv>
              <TableRowDivLookups>
                <TableColumnDiv>Name</TableColumnDiv>
                <TableColumnDiv>ID</TableColumnDiv>
                <TableColumnDiv>Order</TableColumnDiv>
                <TableColumnDiv>Slug</TableColumnDiv>
                <TableColumnDiv>Edit</TableColumnDiv>
              </TableRowDivLookups>

              <UserTableBodyDiv>
                {items.map((row, ix) => {
                  const namePreview =
                    pickLang(row.name || {}, "en") ||
                    pickLang(row.name || {}, "ru") ||
                    row.id;
                  const isEditing = editingId === row.id;

                  return (
                    <UserTableRowDiv key={row.id}>
                      <TableRowDivLookups>
                        <TableColumnDiv>{namePreview}</TableColumnDiv>
                        <TableColumnDiv>{row.id}</TableColumnDiv>
                        <TableColumnDiv>{row.order ?? 0}</TableColumnDiv>
                        <TableColumnDiv>{slugPreview(row)}</TableColumnDiv>
                        <TableColumnDiv>
                          <Div row gap="6px" justifyStart wrap="wrap">
                            {isEditing ? (
                              <Button
                                type="submit"
                                form={lookupEditFormId(row.id)}
                                greenButton
                                width="auto"
                                fontSize="12px"
                                height="auto"
                                padding="5px 10px"
                                disabled={busy}
                              >
                                {saving ? "Saving…" : "Update"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              width="auto"
                              outlineButton
                              fontSize="12px"
                              height="auto"
                              padding="5px 8px"
                              disabled={busy || ix <= 0}
                              onClick={() => moveRow(ix, -1)}
                            >
                              ↑
                            </Button>
                            <Button
                              type="button"
                              width="auto"
                              outlineButton
                              fontSize="12px"
                              height="auto"
                              padding="5px 8px"
                              disabled={busy || ix >= items.length - 1}
                              onClick={() => moveRow(ix, 1)}
                            >
                              ↓
                            </Button>
                            <Button
                              type="button"
                              onClick={() => handleEditToggle(row)}
                              width="auto"
                              outlineButtonGreen
                              fontSize="12px"
                              height="auto"
                              padding="5px 10px"
                            >
                              {isEditing ? "Close" : "Edit"}
                            </Button>
                          </Div>
                        </TableColumnDiv>
                      </TableRowDivLookups>

                      {isEditing ? (
                        <AdminLookupFormPanel
                          title={`Edit — ${namePreview}`}
                          formId={lookupEditFormId(row.id)}
                          formData={editFormData}
                          setFormData={setEditFormData}
                          isEdit
                          onCancel={() => handleEditToggle(row)}
                          onSubmit={handleEditSubmit}
                          onDeleted={handleDelete}
                          saving={saving}
                          deleting={deleting}
                        />
                      ) : null}
                    </UserTableRowDiv>
                  );
                })}
                {items.length === 0 && !loading ? (
                  <TableColumnDiv style={{ padding: 12 }}>
                    No items yet — click Create.
                  </TableColumnDiv>
                ) : null}
              </UserTableBodyDiv>
            </UserTableDiv>
          </Div>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}

export default function AdminArticleFirestoreDictionary({ heading, collectionName }) {
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCheckingAdmin(true);
      setIsAdmin(false);
      if (!user) {
        setCheckingAdmin(false);
        return;
      }
      try {
        const tok = await user.getIdTokenResult(true);
        setIsAdmin(!!tok.claims.admin);
      } finally {
        setCheckingAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  if (checkingAdmin) return <div style={{ padding: 16 }}>Checking admin rights…</div>;
  if (!isAdmin) return <div style={{ padding: 16 }}>403 — Admin access only</div>;

  return <Inner heading={heading} collectionName={collectionName} />;
}
